/**
 * Candidate revisions and the keep/dismiss loop.
 *
 * The loop IS the product. Everything else is scale.
 *
 * Two rules the prototype (task 0008) established by being clickable, and that
 * this module exists to enforce:
 *
 *  1. **A candidate never overwrites the accepted lens.** Preview activates a
 *     revision; only Keep promotes it. Without that, Keep means nothing —
 *     the user has already seen the change and assumes it happened.
 *  2. **Dismissal is durable, against a candidate identity.** Not a UI toggle.
 *     A dismissed candidate that reappears is the Clippy failure.
 */

import type { TLayoutSpec } from '@morph/spec';
import type { Provenance } from './types.js';

export type RevisionStatus = 'candidate' | 'previewing' | 'accepted' | 'dismissed';

export interface Revision {
  readonly id: string;
  readonly spec: TLayoutSpec;
  readonly status: RevisionStatus;
  readonly provenance: Provenance;
  /** Why this was offered, in plain language. The taste model must be
   *  inspectable, and "because it changed" is not an explanation. */
  readonly rationale: string;
}

export interface LensState {
  readonly accepted?: Revision;
  readonly offered?: Revision;
  /**
   * The ARRANGEMENTS the user has rejected, as canonical text. Never offered
   * again.
   *
   * Text and not ids. @Codex, P2 on `8009d5d`: *"this adds only A's 32-bit FNV
   * ID to `dismissed`; any distinct future arrangement B with the same ID is
   * then silently rejected by `offer()` even though the person never dismissed
   * B. The new `sameArrangement()` comparison fixes collisions while both specs
   * are available, but it does not protect this newly recorded dismissal, where
   * the original spec is discarded."*
   *
   * Exactly right, and it is the residual the previous round filed as `0144`
   * rather than fixed: every other decision in this file has both specs in hand
   * and can ask the exact question, and this one had thrown one of them away.
   *
   * **Content rather than a wider identity**, of the two remedies the review
   * offered, because a wider identity would make the control for the sibling
   * fix unable to fail — the control is a real 32-bit collision, and under a
   * 64-bit id those two specs simply get different ids. Storing the text keeps
   * the id at 32 bits, so the same fixture still exercises both.
   *
   * The cost is memory: a canonical spec is kilobytes and this grows per
   * dismissal. It is bounded by a person's own gestures — a dismissal is a
   * button press, not a repaint — so a long session is tens of entries, and the
   * alternative is a list that is wrong about which arrangement it holds.
   */
  readonly dismissed: readonly string[];
}

export const emptyLens = (): LensState => ({ dismissed: [] });

/** A candidate's identity is its CONTENT, not a counter — so the same proposal
 *  arrived at twice is recognised as the same offer, and stays dismissed. */
/**
 * Deterministic serialisation with keys sorted at EVERY level.
 *
 * This used to be `JSON.stringify(spec, Object.keys(spec).sort())`, which
 * reads like "sort the keys for a stable hash" and is not what the second
 * argument does. An array replacer is a property ALLOWLIST, applied at every
 * level of nesting — so passing the top-level keys meant every nested object
 * serialised as `{}`:
 *
 *     {"root":{},"version":"1"}     for every spec that has those two keys
 *
 * Every LayoutSpec therefore had the same identity, and the two rules this
 * module exists to enforce both inverted. `offer` treats a new candidate as
 * *"already what they have"* and stays silent, so **only the first kept drop
 * in a session ever took effect** — task 0116, where every later drop was
 * announced, accepted and reported as a rearrangement while changing nothing.
 * And `dismissed` holds these ids, so dismissing one candidate would have
 * suppressed every future offer: the Clippy failure named at the top of this
 * file, reached from the opposite direction.
 */
const canonicalise = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonicalise);
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(Object.keys(value as object).sort()
      .map((k) => [k, canonicalise((value as Record<string, unknown>)[k])]));
  }
  return value;
};

/** The canonical text an id is taken over. Two specs with the same one are the
 *  same arrangement; `candidateId` is a lossy summary of it. */
export const canonicalText = (spec: TLayoutSpec): string =>
  JSON.stringify(canonicalise(spec));

/**
 * A 32-bit FNV-1a over that text.
 *
 * @Codex, P2 on `4261caa`: *"such collisions become likely across roughly
 * 77,000 distinct inputs and can also be searched deliberately because node IDs
 * and structure influence the hash."* Both true — 77,000 is the birthday bound
 * on 2^32, and a session that repaints on every value write reaches five
 * figures of distinct arrangements without trying.
 *
 * **And the id is deliberately NOT widened, which is the interesting half.**
 * Widening it to 64 bits was written and reverted. Every decision this file
 * makes with an id has both specs in hand, so the exact question is answerable
 * and `sameArrangement` asks it; the hash only decides whether the text
 * comparison is worth running. A wider hash would lower a probability the
 * comparison has already removed — and it would make the control for that
 * comparison **unable to fail**, because the control is a real 32-bit collision
 * and under a 64-bit id the two specs simply get different ids and the code
 * takes the same branch with or without the fix.
 *
 * A guard that cannot go red is this repository's oldest defect, and a
 * probability reduction is not worth trading one for.
 *
 * The one place with no second thing to compare is `dismissed`, which holds ids
 * and not specs: a collision there silently suppresses a candidate nobody
 * refused. That is a real residual and it is filed as task `0144` rather than
 * papered over here, because its fix is to carry the canonical text — not to
 * make the hash longer.
 */
export function candidateId(spec: TLayoutSpec): string {
  const canonical = canonicalText(spec);
  let h = 2166136261;
  for (let i = 0; i < canonical.length; i += 1) {
    h ^= canonical.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `cand_${(h >>> 0).toString(36)}`;
}

/** The same ARRANGEMENT, decided on content rather than on a summary of it.
 *  Guarded by the id first so the text comparison runs only on a match. */
export const sameArrangement = (a: TLayoutSpec, b: TLayoutSpec): boolean =>
  candidateId(a) === candidateId(b) && canonicalText(a) === canonicalText(b);

/**
 * Has this exact arrangement been refused before?
 *
 * The one question `dismissed` exists to answer, asked in one place so the
 * demo's *"that layout was dismissed"* sentence and the silence it explains
 * cannot disagree about what was dismissed — which they would the moment one of
 * them kept comparing ids.
 */
export const wasDismissed = (state: LensState, spec: TLayoutSpec): boolean =>
  state.dismissed.includes(canonicalText(spec));

export function offer(
  state: LensState,
  spec: TLayoutSpec,
  provenance: Provenance,
  rationale: string,
): LensState {
  const id = candidateId(spec);

  // Already rejected, or already what they have. Silence is the correct
  // response to both.
  if (wasDismissed(state, spec)) return state;
  // The TEXT on an equal id, not the id alone. An id is a lossy summary, and
  // two arrangements that collide under it are not "already what they have" —
  // staying silent about the second is task `0116`'s failure reached through a
  // hash rather than through a comparison that was never made.
  if (state.accepted && sameArrangement(state.accepted.spec, spec)) return state;

  return { ...state, offered: { id, spec, status: 'candidate', provenance, rationale } };
}

export function preview(state: LensState): LensState {
  if (!state.offered) return state;
  return { ...state, offered: { ...state.offered, status: 'previewing' } };
}

/** Revert a preview without dismissing. The user looked and wasn't ready to
 *  decide, which is not a rejection and must not be recorded as one. */
export function revert(state: LensState): LensState {
  if (!state.offered) return state;
  return { ...state, offered: { ...state.offered, status: 'candidate' } };
}

export function keep(state: LensState): LensState {
  if (!state.offered) return state;
  const accepted: Revision = { ...state.offered, status: 'accepted' };
  // The old accepted revision is simply replaced. Its id is not dismissed —
  // the user might want it back, and recording it as rejected would be a lie.
  return { accepted, dismissed: state.dismissed };
}

/**
 * Direct manipulation accepts a spec, and dismisses the one it replaced.
 *
 * Decision `0045`, and it is careful to say this is not the revision path:
 *
 * > When direct manipulation accepts a new spec, the pre-drop candidate is
 * > dismissed before another suggestion is selected. Otherwise the
 * > deterministic generator can immediately re-offer the exact undo even if
 * > later revision paths honour `accepted`.
 *
 * > These are two different guards: revision prevents unrelated structure from
 * > being regenerated away, while dismissal prevents a known replaced candidate
 * > from being offered again. Implementing either one is not evidence for the
 * > other.
 *
 * `0015` F38, still binding. A person drags a block, and the next suggestion is
 * the layout they just left — offered back as a fresh idea, because a
 * deterministic generator asked the same question gives the same answer.
 *
 * ## Why this is not `keep(offer(...))`, which is what the demo did
 *
 * Two of `offer`'s guards are wrong for a gesture. It stays silent when the id
 * is in `dismissed`, and again when it equals the accepted id — both correct
 * for an ambient suggestion and both a bug for a drop: a person rearranging
 * back to a layout they once dismissed would find their own hands ignored.
 *
 * And the pre-drop spec is dismissed rather than some other candidate. What
 * the generator would re-propose is the arrangement that was just replaced —
 * which is whatever was ON SCREEN when the hands moved, and that is not always
 * `accepted`.
 *
 * @Codex, P2 on #184: *"When offer B is previewing over accepted layout A, the
 * demo renders and attaches the drop host to B, but this line still identifies
 * A as the layout replaced by a drag. Dragging preview B into layout C
 * therefore dismisses A while leaving B eligible for the deterministic
 * generator to immediately re-offer as the exact undo the direct-manipulation
 * contract prohibits."*
 *
 * Exactly the failure `0015` F38 names, reached through the one case the
 * original reasoning did not enumerate. The comment this replaces argued
 * *"the pending offer is a different candidate and dismissing it would record
 * a rejection the person never made"* — true of an offer nobody is looking at,
 * and false of one they are dragging, because moving a block out of a preview
 * IS the rejection.
 *
 * `activeSpec` already answers "what is the person looking at", and it is the
 * same function the renderer and the drop host go through. Asking it here is
 * what keeps the three from disagreeing about which layout a gesture was made
 * on — which is how they came to disagree in the first place.
 */
export function acceptDirect(
  state: LensState,
  spec: TLayoutSpec,
  provenance: Provenance,
  rationale: string,
): LensState {
  const id = candidateId(spec);
  const from = activeSpec(state);
  const replaced = from ? canonicalText(from) : undefined;
  /**
   * Never the incoming ARRANGEMENT: a drop that lands back on the one it
   * started from would otherwise dismiss what it just accepted.
   *
   * Compared as canonical text, not as ids. @Codex, P2 on `4261caa`: *"When the
   * directly manipulated layout and the layout it replaced collide under the
   * 32-bit FNV `candidateId()`, this equality treats them as the same candidate
   * and skips dismissal even though their canonical specs differ. The
   * deterministic generator may then immediately re-offer the exact pre-drop
   * arrangement, violating the new direct-manipulation contract."*
   *
   * Both specs are right here, so the exact question is answerable and the
   * summary was being asked instead. The id is deliberately not widened — see
   * `candidateId` — because a wider one would make this fix's own control
   * unable to fail.
   */
  const dismissed = from !== undefined && replaced !== undefined
    && !sameArrangement(from, spec)
    && !state.dismissed.includes(replaced)
    ? [...state.dismissed, replaced]
    : state.dismissed;
  return { accepted: { id, spec, status: 'accepted', provenance, rationale }, dismissed };
}

export function dismiss(state: LensState): LensState {
  if (!state.offered) return state;
  return {
    accepted: state.accepted,
    dismissed: [...state.dismissed, canonicalText(state.offered.spec)],
  } as LensState;
}

/** What the user is actually looking at right now. */
export function activeSpec(state: LensState): TLayoutSpec | undefined {
  if (state.offered?.status === 'previewing') return state.offered.spec;
  return state.accepted?.spec;
}
